/**
 * Error Tracking with Sentry
 *
 * Centralized error tracking and reporting for production monitoring.
 * Only enabled in production when VITE_ENABLE_ERROR_TRACKING=true and VITE_SENTRY_DSN is set.
 */

import * as Sentry from '@sentry/react'
import { env } from '@/config/env'

/**
 * Initialize Sentry error tracking
 * Call this once at app startup
 */
export function initErrorTracking(): void {
  // Only initialize in the browser - Sentry browser SDK uses browser-only APIs
  if (typeof window === 'undefined') {
    return
  }

  if (!env.ENABLE_ERROR_TRACKING || !env.SENTRY_DSN) {
    if (env.IS_DEV) {
      console.log('[error-tracking] Sentry disabled in development')
    }
    return
  }

  Sentry.init({
    dsn: env.SENTRY_DSN,
    environment: env.NODE_ENV,
    enabled: env.IS_PROD,

    // Performance Monitoring
    tracesSampleRate: env.IS_PROD ? 0.1 : 1.0, // 10% in production, 100% in dev
    profilesSampleRate: 0.1, // 10% of traces will have profiling

    // Session Replay (optional - captures user sessions)
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true, // Privacy: mask all text
        blockAllMedia: true, // Privacy: block all media
      }),
    ],

    // Release tracking (set during build)
    release: import.meta.env.VITE_SENTRY_RELEASE,

    // Ignore common errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',

      // Network errors (not actionable)
      'NetworkError',
      'Failed to fetch',
      'Load failed',

      // ResizeObserver (benign)
      'ResizeObserver loop',
    ],

    // Before send hook - filter and enrich events
    beforeSend(event, hint) {
      // Don't send events in development
      if (env.IS_DEV) {
        console.error('[error-tracking] Sentry event:', event, hint)
        return null
      }

      // Filter out low-priority errors
      if (event.exception?.values?.some(e => e.value?.includes('Script error'))) {
        return null
      }

      // Add custom context
      event.tags = {
        ...event.tags,
        app_version: import.meta.env.VITE_APP_VERSION || 'unknown',
      }

      return event
    },
  })

  if (env.IS_DEV) {
    console.log('[error-tracking] Sentry initialized')
  }
}

/**
 * Set user context for error tracking
 */
export function setUserContext(user: {
  id: string
  email?: string
  name?: string
  roles?: string[]
}): void {
  if (!env.ENABLE_ERROR_TRACKING) return

  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
    roles: user.roles?.join(','),
  })
}

/**
 * Clear user context (on logout)
 */
export function clearUserContext(): void {
  if (!env.ENABLE_ERROR_TRACKING) return
  Sentry.setUser(null)
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message: string, data?: Record<string, unknown>): void {
  if (!env.ENABLE_ERROR_TRACKING) return

  Sentry.addBreadcrumb({
    message,
    data,
    level: 'info',
    timestamp: Date.now() / 1000,
  })
}

/**
 * Capture exception manually
 */
export function captureException(error: Error, context?: Record<string, unknown>): void {
  if (!env.ENABLE_ERROR_TRACKING) {
    console.error('[error-tracking] Exception:', error, context)
    return
  }

  Sentry.captureException(error, {
    extra: context,
  })
}

/**
 * Capture message (non-error)
 */
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
  if (!env.ENABLE_ERROR_TRACKING) {
    console.log(`[error-tracking] ${level.toUpperCase()}:`, message)
    return
  }

  Sentry.captureMessage(message, level)
}

/**
 * Set custom tags for grouping errors
 */
export function setTags(tags: Record<string, string>): void {
  if (!env.ENABLE_ERROR_TRACKING) return
  Sentry.setTags(tags)
}

/**
 * Set custom context for debugging
 */
export function setContext(name: string, context: Record<string, unknown>): void {
  if (!env.ENABLE_ERROR_TRACKING) return
  Sentry.setContext(name, context)
}

/**
 * Create Sentry Error Boundary for React components
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary

/**
 * Re-export Sentry for advanced usage
 */
export { Sentry }
